export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/bmp,.jpg,.jpeg,.png,.webp,.gif,.bmp';

/* Cap the sampling canvas so huge photos don't blow up memory — picking
   accuracy at this scale is indistinguishable on screen. */
export const MAX_CANVAS_DIM = 4096;

const SUPPORTED_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
]);

const SUPPORTED_EXTENSIONS = /\.(jpe?g|png|webp|gif|bmp)$/i;

export function isSupportedColorPickerFile(file) {
    if (!file) return false;
    if (file.type) return SUPPORTED_TYPES.has(file.type);
    return SUPPORTED_EXTENSIONS.test(file.name || '');
}

function toHexPart(value) {
    return value.toString(16).padStart(2, '0');
}

export function rgbToHex(r, g, b) {
    return `#${toHexPart(r)}${toHexPart(g)}${toHexPart(b)}`.toUpperCase();
}

export function rgbToHsl(r, g, b) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;

    if (max === min) {
        return { h: 0, s: 0, l: Math.round(l * 100) };
    }

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    let h;
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else h = ((rn - gn) / d + 4) / 6;

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
}

/** Build the full color descriptor used across the picker UI. */
export function buildColor(r, g, b) {
    const { h, s, l } = rgbToHsl(r, g, b);
    return {
        r,
        g,
        b,
        h,
        s,
        l,
        hex: rgbToHex(r, g, b),
        rgbText: `rgb(${r}, ${g}, ${b})`,
        hslText: `hsl(${h}, ${s}%, ${l}%)`,
    };
}

const PALETTE_SAMPLE_MAX_DIM = 400;
export const DEFAULT_PALETTE_COUNT = 9;

function getBucketRange(bucket, channel) {
    let min = 255;
    let max = 0;
    for (const px of bucket) {
        if (px[channel] < min) min = px[channel];
        if (px[channel] > max) max = px[channel];
    }
    return max - min;
}

function splitBucket(bucket) {
    const ranges = [0, 1, 2].map((ch) => getBucketRange(bucket, ch));
    const channel = ranges.indexOf(Math.max(...ranges));
    bucket.sort((a, b) => a[channel] - b[channel]);
    const mid = Math.floor(bucket.length / 2);
    return [bucket.slice(0, mid), bucket.slice(mid)];
}

function averageBucketColor(bucket) {
    let r = 0;
    let g = 0;
    let b = 0;
    for (const px of bucket) {
        r += px[0];
        g += px[1];
        b += px[2];
    }
    const len = bucket.length;
    return [Math.round(r / len), Math.round(g / len), Math.round(b / len)];
}

/** Median-cut quantization — returns dominant colors sorted by frequency. */
export function extractPalette(imageData, colorCount = DEFAULT_PALETTE_COUNT) {
    const pixels = [];
    const { data } = imageData;
    const step = Math.max(1, Math.floor(data.length / 4 / 10000)) * 4;

    for (let i = 0; i < data.length; i += step * 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        if (a < 128) continue;
        pixels.push([r, g, b]);
    }

    if (!pixels.length) return [];

    let buckets = [pixels];
    while (buckets.length < colorCount) {
        let maxRange = 0;
        let maxIdx = 0;
        buckets.forEach((bucket, idx) => {
            const range = Math.max(getBucketRange(bucket, 0), getBucketRange(bucket, 1), getBucketRange(bucket, 2));
            if (range > maxRange && bucket.length > 1) {
                maxRange = range;
                maxIdx = idx;
            }
        });
        if (maxRange === 0) break;
        const [left, right] = splitBucket(buckets[maxIdx]);
        buckets.splice(maxIdx, 1, left, right);
    }

    return buckets
        .filter((bucket) => bucket.length > 0)
        .map((bucket) => {
            const [r, g, b] = averageBucketColor(bucket);
            return { ...buildColor(r, g, b), count: bucket.length };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, colorCount);
}

export function extractPaletteFromImage(img, colorCount = DEFAULT_PALETTE_COUNT) {
    const scale = Math.min(1, PALETTE_SAMPLE_MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight, 1));
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, width, height);

    return extractPalette(ctx.getImageData(0, 0, width, height), colorCount);
}
